"use client"

import { Box, Heading, Text, useColorModeValue } from "@chakra-ui/react"
import { motion, useInView } from "framer-motion"
import { useEffect, useRef } from "react"
import { fetchTopCompaniesAsync, selectTopCompanies } from "../../../features/TopCompanies/TopCompaniesSlice"
import { useDispatch, useSelector } from "react-redux"
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import AlternativeLogo from "../logos/fvi1.png"
import 'swiper/css'
import 'swiper/css/autoplay'

const MotionBox = motion(Box)
const MotionHeading = motion(Heading)
const MotionText = motion(Text)

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" },
    },
}

const logoHover = {
    hover: {
        filter: "grayscale(0%)",
        opacity: 1,
        scale: 1.1,
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 10
        }
    }
}

export default function FeaturedEmployers() {
    const ref = useRef(null)
    const isInView = useInView(ref, { once: true, amount: 0.1 })
    const base_url = "https://api.indiahirings.com/uploads/company/"
    const dispatch = useDispatch()
    const companies = useSelector(selectTopCompanies)
    const bgPrimary = useColorModeValue("white", "gray.900")
    const textPrimary = useColorModeValue("gray.800", "white")
    const textSecondary = useColorModeValue("gray.600", "gray.300")

    useEffect(() => {
        dispatch(fetchTopCompaniesAsync())
    }, [dispatch])

    return (
        <MotionBox
            as="section"
            py={{ base: 12, md: 16 }}
            bg={bgPrimary}
            position="relative"
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeIn}
        >
            {/* Background decorative elements */}
            <MotionBox
                position="absolute"
                top="20%"
                left="5%"
                width="200px"
                height="200px"
                borderRadius="full"
                bg="blue.100"
                opacity="0.05"
                zIndex="0"
                animate={{
                    y: [0, -20, 0],
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
            />
            <MotionBox
                position="absolute"
                bottom="10%"
                right="5%"
                width="150px"
                height="150px"
                borderRadius="full"
                bg="purple.100"
                opacity="0.05"
                zIndex="0"
                animate={{
                    y: [0, 20, 0],
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                }}
            />

            <Box px={{ base: 6, md: 12, lg: 24 }} mx="auto" w="full" maxW="none" position="relative" zIndex="1">
                <MotionBox
                    ref={ref}
                    textAlign="center"
                    mb={12}
                    variants={fadeIn}
                >
                    <MotionHeading
                        as="h2"
                        fontSize={{ base: "3xl", md: "4xl" }}
                        fontWeight="bold"
                        color={textPrimary}
                        mb={4}
                        variants={fadeIn}
                    >
                        Trusted by Leading Companies
                    </MotionHeading>
                    <MotionText
                        fontSize={{ base: "lg", md: "xl" }}
                        color={textSecondary}
                        maxW="3xl"
                        mx="auto"
                        variants={fadeIn}
                    >
                        Join thousands of successful businesses that have found their perfect talent match on our platform.
                    </MotionText>
                </MotionBox>

                <MotionBox
                    initial={{ opacity: 0 }}
                    animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 1 }}
                >
                    {Array.isArray(companies) && companies.length > 0 ? (
                        <Swiper
                            modules={[Autoplay]}
                            spaceBetween={30}
                            slidesPerView={2}
                            autoplay={{
                                delay: 2500,
                                disableOnInteraction: false,
                                pauseOnMouseEnter: true
                            }}
                            loop={true}
                            speed={1000}
                            breakpoints={{
                                480: {
                                    slidesPerView: 3,
                                    spaceBetween: 20,
                                },
                                768: {
                                    slidesPerView: 4,
                                    spaceBetween: 30,
                                },
                                1024: {
                                    slidesPerView: 5,
                                    spaceBetween: 40,
                                },
                                1280: {
                                    slidesPerView: 6,
                                    spaceBetween: 50,
                                },
                            }}
                        >
                            {companies.map((company, index) => (
                                <SwiperSlide key={`${company.id || index}`}>
                                    <MotionBox
                                        h="140px" // Increased height
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        filter="grayscale(100%)"
                                        opacity={0.7}
                                        whileHover="hover"
                                        variants={logoHover}
                                        initial={{ opacity: 0.7, scale: 1 }}
                                        animate={{ opacity: 0.7, scale: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <motion.img
                                            src={company.logo ? `${base_url}${company.logo}` : AlternativeLogo.src}
                                            alt={company.company_name || "Company Logo"}
                                            style={{
                                                height: '90px', // Increased size
                                                width: 'auto',
                                                objectFit: 'contain',
                                                maxWidth: '180px', // Increased max width
                                                cursor: 'pointer'
                                            }}
                                            onError={(e) => (e.target.src = AlternativeLogo)}
                                            whileHover={{ scale: 1.1 }}
                                        />
                                    </MotionBox>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        <MotionText
                            textAlign="center"
                            color={textSecondary}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            Loading companies...
                        </MotionText>
                    )}
                </MotionBox>
            </Box>
        </MotionBox>
    )
}